import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { PageHeader } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonSize } from "@/components/Button/types";
import { NewTicketModal } from "@/components/SupportTicket/NewTicketModal";
import { useSnackBar } from "@/utils/useSnackbar";

import { SupportFilterBar } from "./components/SupportFilterBar";
import { SupportTicketsTable } from "./components/SupportTicketsTable";
import { useSupportTickets } from "./hooks/useSupportTickets";
import { SUPPORT_TICKET_TABS, SupportTicketTab } from "./utils/ticketTabs";

/**
 * The customer's support tickets (PREC-3937, spec §7). Reached only with SUPPORT_TICKETS on — the
 * route and the nav entry are both gated, and with the flag off the API answers 403 anyway.
 *
 * The header's help launcher is the mockup's "Ask the assistant" affordance; `PageHeader` renders
 * it on every page, so there is no second button here.
 */
export const Support: FunctionComponent = () => {
	const { t } = useTranslation();
	const controller = useSupportTickets();
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();
	const [isNewTicketOpen, setIsNewTicketOpen] = useState(false);

	const tabs = SUPPORT_TICKET_TABS.map((tab) => ({
		value: tab,
		label: (
			<span className="flex items-center gap-2">
				{t(`support.tabs.${tab}`)}
				<span className="rounded-full bg-status-info-light px-1.5 py-0.5 text-[11px] font-semibold text-text-meta">
					{controller.counts[tab]}
				</span>
			</span>
		),
	}));

	const handleCreated = (ticketId: number, failedAttachment?: string) =>
		handleOpenSnackbar(
			failedAttachment
				? t("support.modal.createdWithFailedUpload", { id: ticketId, name: failedAttachment })
				: t("support.modal.created", { id: ticketId })
		);

	return (
		<div className="flex h-full w-full flex-col">
			<PageHeader
				activeTab={controller.tab}
				actions={
					<Button
						size={ButtonSize.XS}
						title={t("support.newTicket")}
						onClick={() => setIsNewTicketOpen(true)}
					/>
				}
				tabs={tabs}
				title={t("support.title")}
				onTabChange={(value) => controller.setTab(value as SupportTicketTab)}
			/>

			<SupportFilterBar controller={controller} />
			<SupportTicketsTable controller={controller} onNewTicket={() => setIsNewTicketOpen(true)} />

			{isNewTicketOpen && (
				<NewTicketModal onClose={() => setIsNewTicketOpen(false)} onCreated={handleCreated} />
			)}
			{renderMessageSnackbar()}
		</div>
	);
};
